import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { nanoid } from "nanoid";
import useDebounce from "hooks/useDebounce";
import useUsers from "hooks/useUsers";
import { IFilterPagingParams, IUsers } from "utils/api.interfaces";

interface AddOrganizationOwnerProps {
  onOwnerSelected: (user: IUsers) => void;
  currentOwnerId?: string;
}

const AddOrganizationOwner: React.FC<AddOrganizationOwnerProps> = ({
  onOwnerSelected,
  currentOwnerId,
}) => {
  const [selectedUser, setSelectedUser] = useState<IUsers | null>(null);
  const [filterValue, setFilterValue] = useState<string>("");
  const [filterInputLoader, setFilterInputLoader] =
    React.useState<boolean>(false);
  const debouncedValue = useDebounce<string>(filterValue, 500);

  const initialSearchParams: IFilterPagingParams = {
    page: "0",
    filter: "",
    pageSize: "50",
  };

  const { isLoading, data } = useUsers(
    {
      filter: filterValue,
      page: initialSearchParams.page,
      pageSize: initialSearchParams.pageSize,
    },
    {
      refetchOnReconnect: "always",
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  React.useEffect(() => {
    setFilterInputLoader(false);
  }, [debouncedValue]);

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(event.target.value);
    setFilterInputLoader(true);
  };

  const handleUserSelect = (user: IUsers) => {
    setSelectedUser(user);
  };

  const handleSetOwner = () => {
    if (selectedUser) {
      onOwnerSelected(selectedUser);
      // Reset form
      setSelectedUser(null);
      setFilterValue("");
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Set Organization Owner
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Warning: Setting a new owner will replace the current owner. An organization can only have one owner.
        </Alert>
        <Stack direction="row" spacing={4}>
          {/* Left side - User Search */}
          <Stack spacing={2} sx={{ width: "50%" }}>
            <TextField
              margin="dense"
              id="filter"
              label="Search users"
              InputLabelProps={{
                shrink: true,
              }}
              variant="standard"
              onChange={handleChangeFilter}
              value={filterValue}
              sx={{ width: "100%" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="" edge="end">
                      {filterInputLoader ? (
                        <CircularProgress size={15} />
                      ) : (
                        <FilterListIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {filterValue && (
              <List
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                {data?.users.map((user) => (
                  <ListItem
                    key={nanoid(6)}
                    button
                    onClick={() => handleUserSelect(user)}
                    selected={selectedUser?.id === user.id}
                    disabled={currentOwnerId ? user.id === currentOwnerId : false}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                      },
                      paddingTop: "1px",
                      paddingBottom: "1px",
                    }}
                  >
                    <ListItemIcon>
                      <Avatar
                        alt={`${user.firstName} ${user.lastName}`}
                        src={
                          user.imageUrl ? user.imageUrl : user.profileImageUrl
                        }
                        sx={{ width: 40, height: 40, marginRight: "10px" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${user.firstName} ${user.lastName}`}
                      secondary={
                        <>
                          {user.email}
                          {currentOwnerId && user.id === currentOwnerId && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="primary"
                              sx={{ display: "block" }}
                            >
                              (Current Owner)
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>

          {/* Right side - Selected User */}
          <Stack spacing={2} sx={{ width: "50%" }}>
            {selectedUser ? (
              <>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                    src={
                      selectedUser.imageUrl
                        ? selectedUser.imageUrl
                        : selectedUser.profileImageUrl
                    }
                    sx={{ width: 64, height: 64 }}
                  />
                  <Stack>
                    <Typography variant="h6">
                      {selectedUser.firstName} {selectedUser.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.email}
                    </Typography>
                  </Stack>
                </Stack>

                <Typography variant="body1" color="text.secondary">
                  This user will become the owner of the organization. Owners have the highest level of permissions and can manage all aspects of the organization.
                </Typography>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSetOwner}
                  sx={{ mt: 2 }}
                >
                  Set as Owner
                </Button>
              </>
            ) : (
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ height: "100%" }}
              >
                <Typography variant="body1" color="text.secondary">
                  {filterValue
                    ? "Select a user from the search results"
                    : "Search for a user to set as organization owner"}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AddOrganizationOwner;